import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Bell,
  CalendarDays,
  CarFront,
  ClipboardList,
  Download,
  Gauge,
  Phone,
  Plus,
  Route,
  ShieldCheck,
  SquarePen,
  X,
  UserPlus,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { BACKEND_URL as API } from '../../apiConfig';
import { getInternalFleetCopy } from '../../pages/internal/internalFleetI18n';
import { getTokyoDateKey } from '../../utils/tokyoDate';

const goldText = 'text-[#d8b76a]';
const panelClass = 'rounded-lg border border-[#d8b76a]/25 bg-[#090a0a]/80 shadow-[0_0_22px_rgba(216,183,105,0.08)]';
const DASHBOARD_TIME_AXIS_HOURS = ['06:00', '09:00', '12:00', '15:00', '18:00', '22:00'];
const DASHBOARD_TIMELINE_START_HOUR = 6;
const DASHBOARD_TIMELINE_END_HOUR = 22;
const DASHBOARD_TIMELINE_BUFFER_HOURS = 1;

const FleetDashboard = ({ onNavigate, user, language = 'zh' }) => {
  const t = getInternalFleetCopy(language).dashboard;
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [dispatchOverview, setDispatchOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDispatchItem, setSelectedDispatchItem] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, alertsRes, dispatchRes] = await Promise.all([
        axios.get(`${API}/api/internal/dashboard/stats`),
        axios.get(`${API}/api/internal/dashboard/expiry-alerts`),
        axios.get(`${API}/api/internal/dispatch/overview`),
      ]);

      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      setDispatchOverview(dispatchRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalAlerts = () => {
    if (!alerts) return 0;
    return Object.values(alerts).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getExpiredCount = () => {
    if (!alerts) return 0;
    return Object.values(alerts).reduce(
      (sum, arr) => sum + arr.filter((item) => item.status === 'expired').length,
      0
    );
  };

  const exportReport = async () => {
    try {
      const token = localStorage.getItem('internal_token');
      const response = await fetch(`${API}/api/internal/export/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(language === 'ja' ? '出力に失敗しました' : '导出失败');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleet_report_${getTokyoDateKey()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert((language === 'ja' ? '出力に失敗しました: ' : '导出失败: ') + error.message);
    }
  };

  const money = (amount) => `¥${Number(amount || 0).toLocaleString('ja-JP')}`;
  const activeVehicles = stats?.vehicles?.active || 0;
  const totalVehicles = stats?.vehicles?.total || 0;
  const activeDrivers = stats?.drivers?.active || 0;
  const totalDrivers = stats?.drivers?.total || 0;
  const operationTotal = activeVehicles + activeDrivers + getTotalAlerts();
  const stableRatio = operationTotal ? Math.round(((activeVehicles + activeDrivers) / operationTotal) * 100) : 100;
  const assignmentDrivers = dispatchOverview?.drivers || [];
  const arrangedDrivers = assignmentDrivers.filter((driver) => driver.total_count > 0);
  const visibleAssignments = (arrangedDrivers.length > 0 ? arrangedDrivers : assignmentDrivers).slice(0, 8);
  const assignmentTimeline = dispatchOverview?.timeline || [];
  const dashboardTimelineDays = buildDashboardTimelineDays(dispatchOverview?.date_from).slice(0, 2);

  const kpis = [
    { label: t.totalVehicles, value: totalVehicles, unit: t.vehicleUnit, icon: CarFront, sub: `${activeVehicles} ${t.activeSub}` },
    { label: t.activeVehicles, value: activeVehicles, unit: t.vehicleUnit, icon: Gauge, sub: `${stableRatio}% ${t.stableSub}` },
    { label: t.totalDrivers, value: totalDrivers, unit: t.driverUnit, icon: UsersRound, sub: `${activeDrivers} ${t.employedSub}` },
    { label: t.activeDrivers, value: activeDrivers, unit: t.driverUnit, icon: UserPlus, sub: `${stats?.drivers?.fulltime || 0} ${t.fulltimeSub}` },
    { label: t.unpaidThisMonth, value: stats?.finances?.unpaid_count || 0, unit: t.itemUnit, icon: Bell, sub: `${getExpiredCount()} ${t.overdueSub}` },
    { label: t.monthlyRevenue, value: money(stats?.finances?.total_income), unit: '', icon: WalletCards, sub: t.financeSub },
  ];

  const alertGroups = [
    { key: 'license_expiry', label: t.licenseExpiry, icon: ShieldCheck, targetTab: 'drivers' },
    { key: 'insurance_expiry', label: t.insuranceExpiry, icon: ShieldCheck, targetTab: 'vehicles' },
    { key: 'inspection_expiry', label: t.inspectionExpiry, icon: CarFront, targetTab: 'vehicles' },
    { key: 'quarterly_inspection_due', label: t.quarterlyInspection, icon: CalendarDays, targetTab: 'vehicles' },
    { key: 'health_check_expiry', label: t.healthCheckExpiry, icon: UsersRound, targetTab: 'drivers' },
  ];

  const alertRows = alertGroups.flatMap((group) =>
    (alerts?.[group.key] || []).slice(0, 3).map((item) => ({
      group,
      title: item.name || item.plate_number || '未命名',
      date: item.expected_date || item.expiry_date || '-',
      status: item.status,
      id: item.id,
    }))
  );

  const handleUpdateAlert = (item) => {
    onNavigate && onNavigate(item.group.targetTab, {
      focusId: item.id,
      reason: item.group.key,
    });
  };

  const driverRows = [
    { label: t.active, value: stats?.drivers?.active || 0, role: t.available, status: t.normal },
    { label: t.fulltime, value: stats?.drivers?.fulltime || 0, role: t.fixedDuty, status: t.normal },
    { label: t.parttime, value: stats?.drivers?.parttime || 0, role: t.spotDuty, status: t.confirm },
    { label: t.suspended, value: stats?.drivers?.suspended || 0, role: t.stopped, status: t.stoppedStatus },
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#d8b76a] border-t-transparent" />
      </div>
    );
  }

  return (
    <div
      className="internal-concierge-dashboard internal-page-skin space-y-4 text-slate-100"
      data-testid="internal-hotel-concierge-dashboard"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d8b76a]/25 pb-3">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-[0.28em] ${goldText}`}>Shonan International</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-normal text-white">{t.heading}</h1>
          <p className="text-sm text-slate-400">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <span>{new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}</span>
          <span>{new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
          <span className="rounded-full border border-[#d8b76a]/35 px-3 py-1 text-[#d8b76a]">{user?.name || '管理員'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {kpis.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className={`${panelClass} min-h-[112px] p-4`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-[#c9b47b]">{item.label}</p>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="truncate text-3xl font-semibold leading-none text-white">{item.value}</span>
                    {item.unit && <span className="pb-1 text-xs text-slate-400">{item.unit}</span>}
                  </div>
                  <p className="mt-2 truncate text-xs text-slate-400">{item.sub}</p>
                </div>
                <Icon className="h-8 w-8 shrink-0 text-[#d8b76a]" strokeWidth={1.6} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.95fr_1.55fr]">
        <div className={`${panelClass} overflow-hidden`} data-testid="dashboard-driver-arrangements">
          <div className="flex items-center justify-between border-b border-[#d8b76a]/20 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-[#f4e6bd]">{t.driverArrangements}</h2>
              <p className="mt-1 text-xs text-slate-500">
                {t.dispatchRange} / {dispatchOverview?.date_from || '-'} - {dispatchOverview?.date_to || '-'}
              </p>
            </div>
            <span className="rounded-md border border-[#d8b76a]/25 bg-black/35 px-3 py-1 text-xs font-semibold text-[#d8b76a]">
              {dispatchOverview?.totals?.scheduled_drivers || 0} {t.scheduledDrivers}
            </span>
          </div>
          <div className="max-h-[31rem] space-y-3 overflow-auto p-4">
            {visibleAssignments.length === 0 ? (
              <div className="rounded-md border border-[#d8b76a]/15 bg-black/35 p-8 text-center text-sm text-slate-500">
                {t.noAssignments}
              </div>
            ) : visibleAssignments.map((driver) => (
              <div key={driver.driver_id} className="rounded-md border border-[#d8b76a]/18 bg-[linear-gradient(180deg,rgba(18,14,8,0.92),rgba(4,4,3,0.96))] p-3" data-testid="dashboard-driver-timeline-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{driver.driver_name}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {driver.vehicle_plate || driver.driver_phone || t.noVehicle}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 text-xs">
                    <span className="rounded border border-[#d8b76a]/25 px-2 py-0.5 text-[#f4e6bd]">{t.official} {driver.official_count}</span>
                    <span className="rounded border border-slate-600/60 px-2 py-0.5 text-slate-300">{t.extra} {(driver.driver_extra_count || 0) + (driver.dispatcher_extra_count || 0)}</span>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {dashboardTimelineDays.map((day) => (
                    <DashboardDriverTimelineRow
                      key={`${driver.driver_id}-${day.date}`}
                      day={day}
                      items={assignmentTimeline.filter((item) => item.driver_id === driver.driver_id && item.date === day.date)}
                      t={t}
                      onOpenItem={setSelectedDispatchItem}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${panelClass} overflow-hidden`}>
          <div className="flex items-center justify-between border-b border-[#d8b76a]/20 px-5 py-4">
            <h2 className="text-base font-semibold text-[#f4e6bd]">{t.driverList}</h2>
            <div className="flex items-center gap-2 rounded-md border border-[#d8b76a]/20 bg-black/35 px-3 py-1.5 text-xs text-slate-400">
              <UsersRound className="h-4 w-4 text-[#d8b76a]" />
              {t.statusOverview}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead className="bg-[#10100f] text-xs text-[#c9b47b]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">{t.category}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.people}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.role}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.status}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.contact}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#d8b76a]/10">
                {driverRows.map((row) => (
                  <tr key={row.label} className="bg-black/20 text-slate-200">
                    <td className="px-4 py-3 font-medium text-white">{row.label}</td>
                    <td className="px-4 py-3 text-[#f4e6bd]">{row.value} {t.driverUnit}</td>
                    <td className="px-4 py-3">{row.role}</td>
                    <td className="px-4 py-3">
                      <span className="internal-status-badge border border-[#d8b76a]/25 bg-[#132216] text-[#8ee6a6]">{row.status}</span>
                    </td>
                    <td className="px-4 py-3 text-[#d8b76a]">
                      <Phone className="inline h-4 w-4" /> {t.managementDesk}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <div className={`${panelClass} overflow-hidden`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d8b76a]/20 px-5 py-4">
            <h2 className="text-base font-semibold text-[#f4e6bd]">{t.expiryAlerts}</h2>
            <span className="rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-300">
              {getTotalAlerts()} {t.needsAction}
            </span>
          </div>
          {alertRows.length > 0 ? (
            <div className="grid gap-2 p-4 md:grid-cols-2">
              {alertRows.slice(0, 8).map((item, index) => {
                const Icon = item.group.icon;
                return (
                  <div key={`${item.group.key}-${index}`} className="flex items-center justify-between gap-3 rounded-md border border-[#d8b76a]/15 bg-black/35 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Icon className="h-5 w-5 shrink-0 text-[#d8b76a]" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-white">{item.title}</p>
                        <p className="text-xs text-slate-400">{item.group.label}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={item.status === 'expired' ? 'text-sm font-semibold text-red-300' : 'text-sm font-semibold text-[#f4e6bd]'}>
                        {item.date}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleUpdateAlert(item)}
                        className="inline-flex min-h-0 items-center gap-1 rounded-md border border-[#d8b76a]/30 bg-[#d8b76a]/10 px-2.5 py-1 text-xs font-semibold text-[#f4e6bd] transition hover:border-[#d8b76a]/60 hover:bg-[#d8b76a]/18"
                        data-testid={`update-expiry-alert-${item.group.key}-${item.id || index}`}
                        title={`${t.update}${item.group.label}`}
                      >
                        <SquarePen className="h-3.5 w-3.5" />
                        {t.update}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400">{t.allValid}</div>
          )}
        </div>

        <div className={`${panelClass} p-5`}>
          <h2 className="mb-4 text-base font-semibold text-[#f4e6bd]">{t.vehicleStatus}</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              [t.activeVehicles, stats?.vehicles?.active || 0],
              [t.maintenance, stats?.vehicles?.maintenance || 0],
              [t.suspended, stats?.vehicles?.suspended || 0],
              [t.retired, stats?.vehicles?.retired || 0],
            ].map(([label, value]) => (
              <div key={label} className="rounded-md border border-[#d8b76a]/15 bg-black/35 p-3 text-center">
                <p className="text-2xl font-semibold text-[#f4e6bd]">{value}</p>
                <p className="mt-1 text-xs text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${panelClass} grid grid-cols-2 gap-px overflow-hidden bg-[#d8b76a]/20 md:grid-cols-5`}>
        {[
          { label: t.newOrder, icon: ClipboardList, action: () => onNavigate && onNavigate('dispatch') },
          { label: t.dispatchVehicle, icon: CarFront, action: () => onNavigate && onNavigate('vehicles', { action: 'add' }) },
          { label: t.addDriver, icon: UserPlus, action: () => onNavigate && onNavigate('drivers', { action: 'add' }) },
          { label: t.itinerary, icon: Route, action: () => onNavigate && onNavigate('dispatch') },
          { label: t.reportCenter, icon: Download, action: exportReport, testId: 'export-report-btn' },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={item.action}
              data-testid={item.testId}
              className="flex items-center justify-center gap-2 bg-[#090a0a] px-3 py-4 text-sm font-semibold text-[#d8b76a] transition hover:bg-[#17130a]"
            >
              {item.label === t.newOrder ? <Plus className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              <span className="whitespace-nowrap">{item.label}</span>
            </button>
          );
        })}
      </div>

      {selectedDispatchItem && (
        <DispatchItemDetailModal
          item={selectedDispatchItem}
          onClose={() => setSelectedDispatchItem(null)}
          t={t}
        />
      )}
    </div>
  );
};

function DashboardDriverTimelineRow({ day, items, t, onOpenItem }) {
  const isFree = items.length === 0;
  return (
    <div className="grid gap-2 rounded-md border border-[#d8b76a]/16 bg-black/30 p-2 md:grid-cols-[54px_1fr] md:items-center" data-testid="dashboard-driver-timeline-row">
      <div>
        <div className="text-xs font-semibold text-[#f4e6bd]">{day.label}</div>
        <div className="text-[10px] text-slate-500">周{day.weekday}</div>
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-[9px] font-semibold text-[#9f8750]">
          {DASHBOARD_TIME_AXIS_HOURS.map((hour) => (
            <span key={hour}>{hour}</span>
          ))}
        </div>
        <div className="relative h-8 overflow-hidden rounded-md border border-[#d8b76a]/22 bg-[linear-gradient(180deg,rgba(24,20,12,0.95),rgba(7,6,4,0.98))] shadow-inner shadow-black/60">
          <div className="absolute inset-y-0 left-0 right-0 grid grid-cols-5">
            {DASHBOARD_TIME_AXIS_HOURS.slice(0, -1).map((hour) => (
              <div key={hour} className="border-r border-[#d8b76a]/12 last:border-r-0" />
            ))}
          </div>
          {isFree ? (
            <div className="absolute inset-1 flex items-center justify-center rounded border border-[#d8b76a]/16 bg-[#20170b]/70 text-[10px] font-semibold text-[#d8b76a]">
              {t.noNext}
            </div>
          ) : (
            items.map((item, index) => (
              <DashboardTimelineBlock
                key={`${item.type}-${item.id}-${index}`}
                item={item}
                index={index}
                t={t}
                onOpenItem={onOpenItem}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardTimelineBlock({ item, index, t, onOpenItem }) {
  const visualRange = getDashboardTimelineVisualRange(item);
  const start = getDashboardTimelinePercentFromMinutes(visualRange?.startMinutes);
  const end = getDashboardTimelinePercentFromMinutes(visualRange?.endMinutes);
  const width = Math.max(5, end - start);
  const top = 5 + (index % 2) * 12;
  const typeClass = item.type === 'driver_extra'
    ? 'border border-[#f3d98b]/45 bg-[#8a6a24] text-[#fff7dc]'
    : item.type === 'dispatcher_extra'
      ? 'border border-[#d8b76a]/35 bg-[#5a4316] text-[#fff1bf]'
      : 'border border-[#ffe7a3]/60 bg-[linear-gradient(135deg,#f2d77d,#b68b32)] text-[#141006]';
  const summary = `${item.time || '--:--'} ${compactDashboardRoute(item) || t.routeMissing}`;

  return (
    <button
      type="button"
      className={`absolute rounded px-1.5 py-0.5 text-left text-[9px] font-semibold shadow-sm ${typeClass}`}
      style={{
        left: `${start}%`,
        top: `${top}px`,
        width: `max(${Math.min(width, 96 - start)}%, 78px)`,
        maxWidth: `calc(100% - ${start}%)`,
      }}
      title={`${item.date} ${item.time || '--:--'} / ${item.route || ''}`}
      data-testid="dashboard-driver-timeline-block"
      onClick={() => onOpenItem?.(item)}
    >
      <span className="block truncate">{summary}</span>
    </button>
  );
}

function DispatchItemDetailModal({ item, onClose, t }) {
  const detail = item?.type === 'official'
    ? item.order_detail || item
    : item.schedule_detail || item;
  const isOfficialOrder = item?.type === 'official';
  const assignedDriver = detail.assigned_driver || {};
  const assignedFleet = detail.assigned_fleet || {};
  const assignedVehicle = detail.assigned_vehicle || {};
  const route = isOfficialOrder
    ? formatDispatchDetailRoute(detail)
    : formatExtraDispatchDetailRoute(detail);
  const noteText = [
    detail.trip_details,
    detail.other_service_desc,
    detail.dispatch_note,
    detail.remarks,
    detail.admin_memo,
    detail.notes,
  ].filter(Boolean).join('\n\n');

  const rows = [
    ['类型', isOfficialOrder ? '正式订单' : getDashboardExtraTypeLabel(item.type)],
    ['订单号/编号', detail.order_no || detail.confirmation_code || detail.id || item.id || '-'],
    ['状态', detail.status || item.status || '-'],
    ['日期时间', [detail.departure_date || detail.date || item.date, detail.pickup_time || detail.time || item.time].filter(Boolean).join(' ') || '-'],
    ['客户名称', detail.customer_name || item.customer_name || '-'],
    ['客户代码', detail.customer_code || '-'],
    ['客户邮箱', detail.customer_email || detail.user_email || '-'],
    ['电话', detail.phone || detail.customer_phone || '-'],
    ['服务类型', detail.service_type || item.service_type || '-'],
    ['车辆类型', detail.vehicle_type || '-'],
    ['路线', route || item.route || '-'],
    ['航班', detail.flight_number || '-'],
    ['乘客/行李/儿童座椅', isOfficialOrder
      ? `${detail.passenger_count ?? 0} 人 / ${detail.luggage_count ?? 0} 件 / ${detail.child_seat_count ?? 0} 个`
      : '-'],
    ['包车小时', detail.selected_hours || detail.duration_hours || item.selected_hours || item.duration_hours || '-'],
    ['订单金额', detail.price !== undefined && detail.price !== null ? formatDashboardMoney(detail.price) : '-'],
    ['付款状态', detail.payment_status || (detail.payment_already_received ? 'paid' : '-')],
    ['付款方式', detail.payment_method || '-'],
    ['司机代收', detail.driver_collection_amount ? `${formatDashboardMoney(detail.driver_collection_amount)} ${detail.driver_collection_currency || 'JPY'}` : '-'],
    ['司机', assignedDriver.driver_name || detail.assigned_driver_name || item.driver_name || '-'],
    ['司机电话', assignedDriver.driver_phone || detail.assigned_driver_phone || detail.driver_phone || item.driver_phone || '-'],
    ['车队', assignedFleet.fleet_name || assignedDriver.fleet_name || '-'],
    ['车辆', [
      assignedVehicle.plate_number,
      assignedDriver.plate_number,
      detail.vehicle_plate,
      detail.assigned_vehicle_id,
    ].filter(Boolean)[0] || '-'],
    ['派单状态', detail.driver_assignment_status || '-'],
    ['司机端状态', detail.driver_order_status || '-'],
    ['创建时间', detail.created_at || '-'],
    ['更新时间', detail.updated_at || '-'],
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="dispatch-item-detail-modal">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-[#d8b76a]/35 bg-[#070707] shadow-2xl shadow-black/70">
        <div className="flex items-start justify-between gap-3 border-b border-[#d8b76a]/20 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d8b76a]">{t.driverArrangements}</p>
            <h3 className="mt-1 truncate text-lg font-semibold text-white">
              {detail.order_no || detail.confirmation_code || detail.id || item.id || '订单详情'}
            </h3>
            <p className="mt-1 truncate text-xs text-slate-400">{route || item.route || '-'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[#d8b76a]/25 p-2 text-[#d8b76a] transition hover:bg-[#d8b76a]/10"
            aria-label="关闭订单详情"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(([label, value]) => (
              <div key={label} className="min-h-[76px] rounded-md border border-[#d8b76a]/15 bg-black/35 px-3 py-2">
                <p className="text-[11px] font-semibold text-[#9f8750]">{label}</p>
                <p className="mt-1 break-words text-sm font-medium text-slate-100">{value || '-'}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-md border border-[#d8b76a]/15 bg-black/35 px-3 py-3">
            <p className="text-[11px] font-semibold text-[#9f8750]">备注/行程详情</p>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-100">{noteText || '-'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDispatchDetailRoute(detail) {
  const pickup = detail.pickup_location_detail || detail.pickup_location || detail.pickup_address || detail.pickup_area || '';
  const dropoff = detail.dropoff_location_detail || detail.destination || detail.destination_address || detail.dropoff_location || detail.dropoff_area || '';
  return pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff || '';
}

function formatExtraDispatchDetailRoute(detail) {
  const pickup = detail.pickup_location || '';
  const dropoff = detail.dropoff_location || '';
  return pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff || '';
}

function getDashboardExtraTypeLabel(type) {
  if (type === 'dispatcher_extra') return '调度追加';
  if (type === 'driver_extra') return '司机追加';
  return '追加日程';
}

function formatDashboardMoney(amount) {
  return `¥${Number(amount || 0).toLocaleString('ja-JP')}`;
}

function buildDashboardTimelineDays(dateFrom) {
  const start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return [0, 1].map((offset) => {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    const diff = Math.round((date.setHours(0, 0, 0, 0) - today) / 86400000);
    const label = diff === 0 ? '今天' : diff === 1 ? '明天' : `${date.getMonth() + 1}/${date.getDate()}`;
    return {
      date: formatDashboardLocalDate(date),
      label,
      weekday: ['日', '一', '二', '三', '四', '五', '六'][date.getDay()],
    };
  });
}

function formatDashboardLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDashboardTimelineMinutes(time) {
  if (!time) return null;
  const [hourText, minuteText = '0'] = String(time).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (Number.isNaN(hour)) return null;
  return hour * 60 + (Number.isNaN(minute) ? 0 : minute);
}

function getDashboardTimelineDurationHours(item) {
  if (item?.service_type === 'charter') {
    const selectedHours = Number(item.selected_hours || item.duration_hours);
    return Number.isFinite(selectedHours) && selectedHours > 0 ? selectedHours : 8;
  }
  const durationHours = Number(item?.duration_hours);
  return Number.isFinite(durationHours) && durationHours > 0 ? durationHours : 2;
}

function getDashboardTimelineVisualRange(item) {
  const startMinutes = getDashboardTimelineMinutes(item?.time);
  if (startMinutes === null) return null;
  const bufferMinutes = DASHBOARD_TIMELINE_BUFFER_HOURS * 60;
  const durationMinutes = getDashboardTimelineDurationHours(item) * 60;
  return {
    startMinutes: startMinutes - bufferMinutes,
    endMinutes: startMinutes + durationMinutes + bufferMinutes,
  };
}

function getDashboardTimelinePercentFromMinutes(minutes) {
  if (minutes === null || minutes === undefined) return 0;
  const decimalHour = minutes / 60;
  const clamped = Math.max(DASHBOARD_TIMELINE_START_HOUR, Math.min(DASHBOARD_TIMELINE_END_HOUR, decimalHour));
  return ((clamped - DASHBOARD_TIMELINE_START_HOUR) / (DASHBOARD_TIMELINE_END_HOUR - DASHBOARD_TIMELINE_START_HOUR)) * 100;
}

function compactDashboardRoute(item) {
  const route = item?.route;
  if (!route) return '';
  const normalized = String(route).replace(/\s+/g, ' ').trim();
  const parts = normalized.split('→').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const fromArea = inferDashboardRouteArea(parts[0]);
    const toArea = inferDashboardRouteArea(parts[parts.length - 1]);
    if (item?.service_type === 'charter') {
      return `${fromArea || toArea || '区域'}包车`;
    }
    return `${fromArea || compactDashboardLocation(parts[0])}-${toArea || compactDashboardLocation(parts[parts.length - 1])}`;
  }
  const area = inferDashboardRouteArea(normalized);
  if (item?.service_type === 'charter' && area) {
    return `${area}包车`;
  }
  return area || compactDashboardLocation(normalized);
}

function compactDashboardLocation(value) {
  const first = String(value || '')
    .split(/[、,，/／|｜]/)[0]
    .split(/\s+/)[0]
    .split('　')[0]
    .trim();
  return first.length > 6 ? `${first.slice(0, 6)}...` : first;
}

function inferDashboardRouteArea(value) {
  const text = String(value || '').toLowerCase();
  const areaRules = [
    { label: '成田', patterns: ['narita', 'nrt', '成田'] },
    { label: '羽田', patterns: ['haneda', 'hnd', '羽田'] },
    { label: '东京', patterns: ['tokyo', '東京', '新宿', '銀座', 'ginza', 'shinjuku', '渋谷', 'shibuya', '浅草', 'asakusa', '上野', 'ueno', '品川', 'shinagawa', '丸の内', 'marunouchi', '六本木', 'roppongi', 'お台場', 'odaiba', '舞浜', 'maihama', 'disney'] },
    { label: '横滨', patterns: ['yokohama', '横浜'] },
    { label: '箱根', patterns: ['hakone', '箱根', '強羅', 'gora', '小田原', 'odawara'] },
    { label: '日光', patterns: ['nikko', '日光', '東照宮', 'toshogu'] },
    { label: '川越', patterns: ['kawagoe', '川越'] },
    { label: '富士', patterns: ['fuji', '富士', '河口湖', 'kawaguchiko', '山中湖', 'yamanakako'] },
    { label: '静冈', patterns: ['shizuoka', '静岡', '清水', 'shimizu'] },
    { label: '大阪', patterns: ['osaka', '大阪'] },
    { label: '京都', patterns: ['kyoto', '京都'] },
    { label: '名古屋', patterns: ['nagoya', '名古屋'] },
  ];
  const matched = areaRules.find((rule) => rule.patterns.some((pattern) => text.includes(pattern.toLowerCase())));
  return matched?.label || '';
}

export default FleetDashboard;
